/* eslint-disable multiline-ternary */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React from 'react'
import Image from 'next/image'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'

interface Props {
  content: any[]
  strapiBaseUrl?: string
}

function getStrapiMedia (src: string | undefined, baseUrl?: string): string {
  if (src == null) return ''
  if (/^https?:\/\//.test(src)) return src
  if (baseUrl != null) return baseUrl.replace(/\/$/, '') + (src.startsWith('/') ? src : '/' + src)
  return src
}

export default function StrapiRichRenderer ({ content, strapiBaseUrl }: Props): JSX.Element | null {
  if (!content) return null

  return (
    <div className="-mx-2 pl-3 pr-5">
      <BlocksRenderer
        content={content}
        // block render overrides
        blocks={{
          // headings (h1..h6)
          heading: ({ children, level }: any) => {
            const cls =
              level === 1
                ? 'text-[24px] md:text-[28px] font-bold text-[#111] mt-6 mb-3'
                : level === 2
                  ? 'text-[20px] md:text-[22px] font-bold text-[#111] mt-6 mb-3'
                  : level === 3
                    ? 'text-[17px] md:text-[18px] font-semibold text-[#111] mt-5 mb-2'
                    : 'text-[16px] font-semibold text-[#111] mt-4 mb-2'

            const Tag = `h${level}` as any
            return <Tag className={cls}>{children}</Tag>
          },

          paragraph: ({ children }: any) => {
            return <p className="text-[15px] md:text-[16px] text-[#444] leading-relaxed mb-3">{children}</p>
          },

          list: ({ children, format }: any) => {
            const base = 'ml-6 mb-3'
            if (format === 'ordered') {
              return <ol className={`list-decimal ${base} space-y-2 text-[15px] text-[#444]`}>{children}</ol>
            }
            return <ul className={`list-disc ${base} space-y-2 text-[15px] text-[#444]`}>{children}</ul>
          },

          'list-item': ({ children }: any) => {
            return <li className="leading-relaxed">{children}</li>
          },

          quote: ({ children }: any) => {
            return (
              <blockquote className="border-l-4 border-[#e5e7eb] bg-[#fafafa] pl-4 py-2 italic text-[#555] mb-4">
                {children}
              </blockquote>
            )
          },

          // hr: () => <hr className="border-t border-[#e5e7eb] my-6" />,

          // code block
          code: ({ value }: any) => {
            // const language = value?.language || ''
            return (
              <pre className="bg-[#0f172a] text-[#e6eef8] rounded-md p-4 overflow-x-auto text-sm mb-4">
                <code>{value?.code ?? value}</code>
              </pre>
            )
          },

          image: (props: any) => {
            const value = props?.value ?? props
            const url =
              value?.url ??
              value?.data?.url ??
              value?.src ??
              (value?.attributes?.url) ??
              undefined
            const alt = value?.alt ?? value?.caption ?? value?.title ?? ''
            const caption = value?.caption ?? ''

            const src = getStrapiMedia(url, strapiBaseUrl)

            const width = value?.width ?? value?.data?.width
            const height = value?.height ?? value?.data?.height

            if (!src) return null

            return (
              <figure className="my-4">
                <div className="relative w-full max-h-[600px] overflow-hidden rounded">
                  {width && height ? (
                    <Image src={src} alt={alt || 'image'} width={width} height={height} style={{ objectFit: 'cover' }} />
                  ) : (
                    <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                      <Image src={src} alt={alt || 'image'} fill style={{ objectFit: 'cover' }} />
                    </div>
                  )}
                </div>
                {caption && <figcaption className="text-sm text-[#666] mt-2">{caption}</figcaption>}
              </figure>
            )
          },

          link: ({ children, url, openInNewTab }: any) => {
            const isExternal = /^https?:\/\//.test(url)
            return (
              <a
                className="text-[#d63384] underline"
                href={url}
                target={openInNewTab || isExternal ? '_blank' : undefined}
                rel={openInNewTab || isExternal ? 'noopener noreferrer' : undefined}
              >
                {children}
              </a>
            )
          }
        }}
      />
    </div>
  )
}
